"use client";
import React from "react";
import { StaticImageData } from "next/image";
import IndustryBenefitGridCard from "./IndustryBenefitGridCard";
import IndustryBenifitsHeaderSection from "./IndustryBenifitsHeaderSection";

export type BenefitItem = {
  icon?: StaticImageData | string | undefined;
  title: string[] | string;
  description: string;
};

export type HeaderConfig = {
  title: React.ReactNode;
  subtitle: string;
  description: string;
  buttonText?: string;
  mode?: "dark" | "light" | string;
  layoutVariant?: 1 | 2;
};

export type IndustryBenifitsGridProps = {
  id?: string;
  header: HeaderConfig;
  benefits: BenefitItem[];
  ctaButton?: React.ReactNode;
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Controls the color scheme of the entire section. Defaults to "dark". */
  theme?: "dark" | "light";
};

export default function IndustryBenifitsGrid({
  id = "industry-benifits-grid",
  header,
  benefits,
  ctaButton,
  columns = 4,
  theme = "dark",
}: IndustryBenifitsGridProps) {
  const gridColumns = {
    1: "lg:grid-cols-1",
    2: "lg:grid-cols-2",
    3: "lg:grid-cols-3",
    4: "lg:grid-cols-4",
    5: "lg:grid-cols-5",
    6: "lg:grid-cols-6",
  };

  const isDark = theme === "dark";

  return (
    <section
      id={id}
      className={`w-full max-w-[100vw] relative z-2 overflow-hidden py-[80px] lg:py-[120px] transition-colors duration-300 ${isDark ? "bg-[#000000]" : "bg-[#d9e2ed]"
        }`}
    >
      {/* Ambient glow blobs — visible only in dark mode */}
      {isDark && (
        <>
          <div className="w-[600px] h-[600px] bg-[#225bd4]/30 rounded-full blur-[280px] absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/3 pointer-events-none" />
          <div className="w-[600px] h-[600px] bg-[#225bd4]/30 rounded-full blur-[280px] absolute left-0 bottom-0 -translate-x-1/3 translate-y-1/3 pointer-events-none" />
        </>
      )}

      {/* Subtle tint blobs for light mode */}
      {!isDark && (
        <>
          <div className="w-[600px] h-[600px] bg-[#225bd4]/15 rounded-full blur-[280px] absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/3 pointer-events-none" />
          <div className="w-[600px] h-[600px] bg-[#225bd4]/15 rounded-full blur-[280px] absolute left-0 bottom-0 -translate-x-1/3 translate-y-1/3 pointer-events-none" />
        </>
      )}

      <div className="max-w-[2500px] mx-auto w-[calc(100%_-_10%)] relative z-10">
        {/* Header spanning full width, title & subtitle stacked vertically */}
        <IndustryBenifitsHeaderSection
          header={header}
          ctaButton={ctaButton}
          theme={theme}
        />

        {/* Responsive grid layout */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 ${gridColumns[columns]} gap-x-8 gap-y-16 mt-16 lg:mt-24`}
        >
          {benefits.map((item, index) => (
            <IndustryBenefitGridCard
              key={index}
              item={item}
              index={index}
              theme={theme}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
